import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@material-ui/core";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { DateTime } from "luxon";

// 公開日から何日以内を「新着」と判定するか
const NEW_THRESHOLD_DAYS = 7;

const isNew = (publishedAt?: string) => {
  if (!publishedAt) return false;
  const pub = DateTime.fromISO(publishedAt);
  if (!pub.isValid) return false;
  const diffDays = DateTime.local().diff(pub, "days").days;
  return diffDays <= NEW_THRESHOLD_DAYS;
};

// システムお知らせ設定
// 変更時はここを編集してビルド＆デプロイ
const NOTICES: {
  id: string;
  title: string;
  body: string;
  color: string;
  publishedAt: string; // ISO 日付 (yyyy-MM-dd)、7日以内で NEW バッジ表示
}[] = [
  {
    id: "guest-update-and-csv-call-20260422",
    publishedAt: "2026-04-22",
    title: "【改善】顧客情報更新の挙動修正 + 受注 CSV にコール時刻列追加",
    body:
      "顧客情報更新画面の不具合を修正し、受注 CSV 出力に「コール時刻」列を追加しました。\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "■ 顧客情報更新画面の改善\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "・電話番号・メールアドレスが他の顧客と重複する場合、\n" +
      "  対象顧客名を含むダイアログを表示 (例:「入力された電話番号は\n" +
      "  すでに登録済みです (顧客名: ○○様)」)\n" +
      "・エラー時はモーダルが開いたままになり、入力をやり直せます\n" +
      "  (従来は閉じてしまって変更がどこに消えたか分からない状態)\n" +
      "・メールアドレスを変更しても反映されない不具合が解消\n" +
      "  (顧客区分を変えない場合に保存処理がスキップされていた問題)\n" +
      "・今後、同企業内で同じメールアドレスを複数顧客に登録できません\n" +
      "  (会員ページログイン ID として使われるため)\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "■ 受注 CSV「コール時刻」列追加\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "・受注一覧画面に表示されている「call」(コール時刻) 列が\n" +
      "  CSV 出力に含まれていなかった不具合を修正\n" +
      "・「実終了」列の次に「コール時刻」列が追加されます\n" +
      "・画面表示と同じロジック:\n" +
      "  実終了 (なければ予定 OUT) から店舗設定の\n" +
      "  「アラート発信分前」を引いた時刻 (HH:mm)\n" +
      "・店舗のアラート設定が未設定の場合は空欄\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "■ 反映\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "・本日より本番反映済み、追加作業は不要です\n" +
      "・ブラウザキャッシュが残る場合はハードリロード\n" +
      "  (Ctrl+Shift+R / Cmd+Shift+R) で即反映",
    color: "bg-gradient-to-r from-indigo-500 to-sky-500",
  },
  {
    id: "cast-mobile-new-ui-beta-20260421",
    publishedAt: "2026-04-21",
    title: "【β公開】キャストモバイル新 UI を公開しました",
    body:
      "キャスト向けモバイルアプリの UI を全面刷新、β 版として公開しました。\n" +
      "既存 cast.no-tel.com の Top 画面にバナーが出るので、キャストさんはタップで新 UI を試せます。\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "■ 主な変更点\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "・5 タブ構成 (今日 / シフト / 顧客 / 精算 / 自分)\n" +
      "・今日タブの予約カードは時刻順 + ステータスバッジ\n" +
      "・予約は履歴から複製で日時だけ変えて申請可\n" +
      "・精算画面は予約中・接客中も含めて期間合計が見える\n" +
      "・LINE 連携で予約通知を LINE で受け取り可\n" +
      "・緊急連絡 FAB: 至急来てください/連絡ください/退出します\n" +
      "  → 店舗側はヘッダーで最大 15 秒以内に検知\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "■ お店様へのお願い\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "・オプトイン方式です。キャストさん自身が試して、続けるかは本人判断\n" +
      "・感想は新 UI の「自分」タブから匿名送信される仕組み\n" +
      "・不具合・改善要望は運営が直接確認してカテゴリ分けします\n" +
      "・正式移行 (cast.no-tel.com → 新 UI) は β のフィードバック次第\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "■ 詳細\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "詳しい機能紹介・スクショはブログ記事をご覧ください:\n" +
      "https://tasteck.tech/blog/2026-04-21-cast-mobile-new-ui-beta",
    color: "bg-gradient-to-r from-emerald-500 to-teal-500",
  },
  {
    id: "db-calendar-speedup-20260420",
    publishedAt: "2026-04-20",
    title: "【スピード大改善】ゲストカレンダーが爆速になりました (1,467倍)",
    body:
      "ゲストモバイルの予約カレンダーで「読み込み中のまま」になる問題が完全解消しました。\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "■ 改善内容 (体感で激変)\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "・ゲストカレンダー予約取得: 48秒 → 33ms (約 1,467 倍)\n" +
      "・キャストシフト表示: 2.5秒 → 3ms (約 850 倍)\n" +
      "・ダッシュボード集計: 24秒 → 33ms (約 700 倍)\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "■ 何を変えたか (裏側)\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "1. 新 index 追加 — ダッシュボード集計が効率化\n" +
      "2. 予約カレンダーのクエリを 2 段階化 — MySQL 8 の罠を回避\n" +
      "3. シフトクエリの OR 条件を書き換え — 重なり判定に変更\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "■ 影響\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "・完全に透過的な改善、操作方法は一切変わりません\n" +
      "・無停止デプロイ済み、再ログイン不要\n" +
      "・キャストのシフト表示もダッシュボードも速くなっています\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "■ 発見の経緯\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "昨日入れたばかりの計測基盤 (slow_query_log) が 24 時間で\n" +
      "真犯人 (48 秒のクエリ) を可視化。過去半年続けていた\n" +
      "「場当たり index 追加」では絶対に辿り着けなかった診断でした。\n" +
      "\n" +
      "技術的な詳細はブログ記事をご覧ください:\n" +
      "https://tasteck.tech/blog/2026-04-20-query-rewrite-1467x-speedup",
    color: "bg-gradient-to-r from-indigo-500 to-cyan-500",
  },
  {
    id: "guest-app-rebuild-20260419",
    publishedAt: "2026-04-19",
    title: "【ゲストモバイル刷新】全面リニューアルしました",
    body:
      "顧客モバイル (guest.no-tel.com) を Next.js 16 + 最新 UX 研究ベース設計で全面リニューアルしました。\n" +
      "\n" +
      "URL は変わりません — 既存のお客様がそのままログインしてご利用いただけます。\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "■ 主な改善点\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "1. お気に入りキャストの 1 週間シフトをカード展開で即確認\n" +
      "2. 利用履歴からワンタップで再予約 (同キャスト / 同店舗)\n" +
      "3. お知らせ既読管理が正常動作 (旧アプリでは未読のまま溜まるバグを修正)\n" +
      "4. カレンダーの予約枠除外バグ修正 (別店舗の同一キャストの予約も除外)\n" +
      "5. 営業日 6 時切替で深夜帯の予約枠が正常に表示\n" +
      "6. 体感速度の大幅改善 (Vercel 東京リージョン化 + クライアントキャッシュ)\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "■ お客様への影響\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "・URL、ログイン情報、ポイント、予約履歴はすべて引き継がれます\n" +
      "・初回表示時はブラウザキャッシュが原因で旧画面が表示される場合があります\n" +
      "  → ハードリロード (Ctrl+Shift+R / Cmd+Shift+R) で解消します\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "■ 技術詳細\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "旧 React (Vite) アプリから Next.js 16 App Router に完全移行。\n" +
      "Hick's Law / Choice Overload / Progressive Disclosure といった\n" +
      "UX 論文を設計指針に採用。旧担当者のコードに潜んでいた\n" +
      "サーバーバグ 3 件 (お知らせ非表示、既読反転、予約枠誤表示) も\n" +
      "この機会に修正済みです。\n" +
      "\n" +
      "詳細はブログ記事をご参照ください:\n" +
      "https://tasteck.tech/blog/2026-04-19-guest-app-rebuild",
    color: "bg-gradient-to-r from-teal-500 to-emerald-500",
  },
  {
    id: "brand-refresh-20260415",
    publishedAt: "2026-04-15",
    title: "【リブランディング】ロゴ・配色を一新しました",
    body:
      "公式サイト (tasteck.tech) の全面リニューアルに合わせて、管理画面のロゴ・配色も新しいブランドアイデンティティに統一しました。\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "■ 変更の意図\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "1. 目に優しい・負担を減らす\n" +
      "   管理画面は 1 日 8 時間以上ご覧になるツールです。長時間の使用でも疲れにくいよう、明度・彩度を抑えたトーンに全面調整しました。\n" +
      "\n" +
      "2. ブランドリデザイン\n" +
      "   tasteck.tech の刷新と合わせて、管理画面もロゴ・配色を最新のブランドに揃えました。\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "■ 具体的な変更内容\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "・ロゴ: 円角コンテナ + コーラルアクセント の新デザイン\n" +
      "・主配色: 落ち着いた深いティール (従来より彩度を抑え、目に優しい色に)\n" +
      "・警告色: 削除など重要操作は赤を採用し、視覚的に区別\n" +
      "・背景: 純白ではなく cool グレートーン (目の負担を軽減)\n" +
      "・カード: 背景と階層感が出る淡いトーン\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "■ ファビコン・モバイルアイコン\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "ブラウザのタブ・ホーム画面ショートカット用アイコンも新ロゴに更新しました。\n" +
      "ブラウザキャッシュが残って反映されない場合は、ハードリロード (Ctrl+Shift+R) またはシークレットウィンドウで開いてください。",
    color: "#115e59",
  },
  {
    id: "payoff-csv-20260415",
    publishedAt: "2026-04-15",
    title: "【機能追加】精算画面に CSV エクスポート追加",
    body:
      "精算画面で、表示中の期間のデータをそのまま CSV ダウンロードできるようになりました。\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "■ 操作方法\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "「精算」画面 → 開始日／終了日の右側にある「精算CSV」ボタン → ポップアップで日付選択 → ダウンロード\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "■ 出力される列（14項目）\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "日付 / 顧客名 / 顧客電話番号 / 店名 / 源氏名 / コース / ステータス /\n" +
      "稼働 / 支払い / 店舗売上 / キャスト報酬 / 全体売上 / 回収 / 精算\n" +
      "\n" +
      "BOM 付き UTF-8 で出力されるため、Excel でそのまま開いても文字化けしません。\n" +
      "最大 31 日まで指定可能です。",
    color: "#0d9488",
  },
  {
    id: "daynav-datepicker-20260414",
    publishedAt: "2026-04-14",
    title: "【機能改善】日付ナビで遠い日付へ直接ジャンプ可能に",
    body:
      "キャストスケジュール・配車（ドライバースケジュール）をはじめ、\n" +
      "日付ナビ [◀] [▶] を使う全画面で、遠い日付の予約登録がしやすくなりました。\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "■ 変更内容\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "日付表示（例: 「2026年4月14日(月)」の部分）をクリックすると、\n" +
      "カレンダーが開いて好きな日付に一発でジャンプできるようになりました。\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "■ 改善の背景\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "お客様からの日付指定予約登録時に、[▶] ボタンを何度もクリック\n" +
      "する必要があり負担が大きいというご要望を多数いただいたため、\n" +
      "迅速に改修いたしました。\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "■ 対象画面\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "・キャストスケジュール\n" +
      "・ドライバースケジュール（配車画面）\n" +
      "・キャストシフト / ドライバーシフト\n" +
      "・受注一覧\n" +
      "・TOP (変更履歴)\n" +
      "\n" +
      "今後も改善のご要望をお寄せください。",
    color: "#388e3c",
  },
  {
    id: "light-plan-20260414",
    publishedAt: "2026-04-14",
    title: "【新プラン】ライトプラン(月額5,000円) を新設しました",
    body:
      "フリープランより上位、スタンダードプランより手頃な「ライトプラン」を新設いたしました。\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "■ 月額料金\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "5,000円（税抜）\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "■ 機能内容\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "現フリープランと同等の機能制限に加え、以下を追加:\n" +
      "・掲示板（社内情報共有）\n" +
      "・AIサポート（マニュアル質問への AI 回答）\n" +
      "\n" +
      "他のプラン仕様は、ご利用マスター → 管理者機能 → プラン変更 画面よりご確認いただけます。\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "■ お申し込み方法\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "プラン変更画面のライト列から「このプランにする」をクリックしてください。カード決済またはコンビニ決済でお手続き可能です。\n" +
      "\n" +
      "ご不明点はこのお知らせに関する問合せとしてご返信ください。",
    color: "#0288d1",
  },
  {
    id: "payment-improvement-20260414",
    publishedAt: "2026-04-14",
    title: "【機能改善】決済処理のエラー修正・UX改善",
    body:
      "プラン変更画面の決済処理について、以下の改善を行いました。\n" +
      "\n" +
      "・新規カード登録時にまれに発生していたエラーを修正\n" +
      "・送信ボタン押下時に「処理中...」表示を追加、多重送信を防止\n" +
      "・エラー発生時に具体的なエラー内容を表示するように改善\n" +
      "\n" +
      "これまでに決済でお困りの場面に遭遇された方は、安心して再度お試しください。",
    color: "#7b1fa2",
  },
  {
    id: "release-20260414",
    publishedAt: "2026-04-14",
    title: "【4/14 アップデート】TOP画面刷新・日付ナビ・PDF復活ほか",
    body:
      "本日のアップデート内容と意図をまとめてご案内します。\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "📊 TOP画面のダッシュボード化\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "本日の受注件数・売上・シフト数、今後7日間の出勤予定/予約件数、対応が必要な項目、本日のキャストシフト一覧、お知らせ（このボックスです）、変更履歴を 1 画面に集約しました。\n" +
      "\n" +
      "意図: ログイン直後に店舗の運営状況が一目で把握できるようにし、日々の確認作業を効率化します。表示パフォーマンスも専用API で大幅改善しました。\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "📅 シフト・受注画面に日付ナビ + CSV出力\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "対象: キャストシフト / 受注 / ドライバーシフト / キャストスケジュール / ドライバースケジュール / 変更履歴\n" +
      "\n" +
      "・[◀] [▶] で日付を1日ずつ移動する UI に統一\n" +
      "・長期データは CSV ボタン → 期間指定 → ダウンロード（最大31日、Excel で文字化けしない UTF-8 BOM 付き）\n" +
      "・シフト・スケジュール画面では未来日も選択可能\n" +
      "\n" +
      "意図: 従来の無制限日付範囲指定によるサーバー負荷を軽減しつつ、月次レポート等の長期データは CSV で取得できるようにしました。\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "📄 支払通知書 PDF の出力復活\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "精算画面の「支払通知書発行」ボタンから PDF のダウンロードが再び利用可能になりました。表示内容（日付・顧客・源氏名・コース・各種料金・合計・メッセージ）は従来通りです。\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "🤖 タスクページを AIサポートに変更\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "旧タスクページを NotebookLM（Google の AI チャット）への入り口に変更しました。マニュアルに基づく質問に 24時間いつでも回答します。\n" +
      "\n" +
      "今後: tasteck 内蔵の AI チャット（店舗の実データを参照できる）へと発展させていく予定です。\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "🏨 マスター管理画面の表示高速化\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "ホテル・ドライバー管理画面にページネーションと名前検索を追加しました。大量データがある店舗でも素早く表示されるようになっています。\n" +
      "\n" +
      "━━━━━━━━━━━━━━━━━━━━\n" +
      "引き続き tasteck をよろしくお願いいたします。",
    color: "#6a1b9a",
  },
  {
    id: "line-202604",
    publishedAt: "2026-04-10",
    title: "【新機能】LINE通知機能が追加されました",
    body:
      "キャスト/ドライバー宛の予約確定通知を LINE で送れるようになりました。\n" +
      "\n" +
      "マスター → LINE設定 から、公式アカウントの設定情報（チャネルアクセストークン・シークレット・ベーシックID）を登録してください。\n" +
      "\n" +
      "キャストやドライバー本人は、モバイル画面からキー発行 → LINE へ送信することで自動的に紐付けが完了します。",
    color: "#1976d2",
  },
  {
    id: "maintenance-20260413-done",
    publishedAt: "2026-04-13",
    title: "【完了】4月13日のデータベースアップデート完了",
    body:
      "予定通り MySQL のアップグレードが正常に完了しました。ご協力ありがとうございました。\n" +
      "\n" +
      "今回の作業により、長期的なコスト削減と性能改善が実現しました。引き続き tasteck をよろしくお願いいたします。",
    color: "#2e7d32",
  },
];

/**
 * ダッシュボード内「タステックからのお知らせ」カード。
 * タイトル一覧表示 → クリックで本文をダイアログ表示。
 * 件数が増えたら内側スクロール。
 */
const SystemNotice: React.FC = () => {
  const [openNoticeId, setOpenNoticeId] = useState<string | null>(null);

  const visibleNotices = NOTICES;
  const openNotice = NOTICES.find((n) => n.id === openNoticeId);

  return (
    <Card>
      <CardContent>
        <Typography
          variant="subtitle1"
          style={{ fontWeight: "bold", marginBottom: 12 }}
        >
          📢 タステックからのお知らせ
        </Typography>
        {visibleNotices.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            現在お知らせはありません
          </Typography>
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            style={{
              gap: 8,
              maxHeight: 220,
              overflowY: "auto",
              paddingRight: 4,
            }}
          >
            {visibleNotices.map((notice) => (
              <Box
                key={notice.id}
                display="flex"
                alignItems="center"
                style={{
                  borderLeft: `4px solid ${notice.color}`,
                  backgroundColor: "#f8f9fa",
                  borderRadius: 4,
                  cursor: "pointer",
                  transition: "background-color 0.15s",
                }}
                onClick={() => setOpenNoticeId(notice.id)}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#eef2f5")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "#f8f9fa")
                }
              >
                <Box
                  flexGrow={1}
                  padding="8px 12px"
                  display="flex"
                  alignItems="center"
                  style={{ gap: 8 }}
                >
                  {isNew(notice.publishedAt) && (
                    <Chip
                      label="NEW"
                      size="small"
                      style={{
                        backgroundColor: "#e53935",
                        color: "#fff",
                        fontWeight: "bold",
                        fontSize: 10,
                        height: 20,
                      }}
                    />
                  )}
                  <Typography
                    variant="body2"
                    style={{ color: notice.color, fontWeight: 500 }}
                  >
                    {notice.title}
                  </Typography>
                </Box>
                <ChevronRightIcon
                  fontSize="small"
                  style={{ color: "#bbb", marginRight: 8 }}
                />
              </Box>
            ))}
          </Box>
        )}
      </CardContent>

      <Dialog
        open={!!openNoticeId}
        onClose={() => setOpenNoticeId(null)}
        fullWidth
        maxWidth="sm"
      >
        {openNotice && (
          <>
            <DialogTitle
              style={{
                borderLeft: `6px solid ${openNotice.color}`,
                paddingLeft: 18,
              }}
            >
              <span style={{ color: openNotice.color, fontWeight: "bold" }}>
                {openNotice.title}
              </span>
            </DialogTitle>
            <DialogContent dividers>
              <Typography
                variant="body2"
                style={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}
              >
                {openNotice.body}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenNoticeId(null)} color="primary">
                閉じる
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Card>
  );
};

export default SystemNotice;
